import { Controller } from '@hotwired/stimulus';
import { driver } from 'driver.js';
import tours from '../tours';
import { trackEvent } from '../analytics';
import { csrfToken } from './helpers/general';

// Generic driver.js tour runner. To add a tour: export { mount(ctx) } from
// app/javascript/tours/<name>_tour.js, register it in tours/index.js, and attach
// data-controller="tour" data-tour-name-value="<name>" (plus optional data-tour-payload-value JSON).
// mount() decides whether to run, calls ctx.start(driverConfig) and returns a cleanup function.
// Every tour plays only when a tour-launcher control asks for it by name (#launch) — nothing mounts on
// arrival, so there is no once-per-browser marker and no run the visitor did not ask for.
// data-tour-completion-url-value is PATCHed once a run reaches step 2 — see #recordWatched.
// A step must not define popover.onPopoverRender: driver.js takes a step's over the config's, and the one
// here is what reports every step and injects the skip button. Steps own onHighlightStarted/onHighlighted.

// driver.js has no text for its close button and its footer only renders previous/next, so this is
// injected — first step only, where the visitor has not engaged yet and the footer holds nothing but the
// progress count.
const SKIP_TEXT = 'Skip tutorial';
const SKIP_CLASS = 'tour-skip-btn';

// The bar a completed run lands in, alongside the "<n> of <total>" ones every other ending produces.
const COMPLETED_LABEL = 'Completed';

// The one `via` whose event races the document's own teardown, so the only one that needs a beacon.
const LEFT_PAGE_VIA = 'left_page';

// Reaching this step is what counts as taken, not finishing it: the card that offers the tour has no other
// dismissal, so a visitor who starts it and closes on step 3 would be re-offered it every load for a week.
const COMPLETION_STEP_INDEX = 1;

const DRIVER_DEFAULTS = {
  popoverClass: 'unscreen-tour-popover',
  overlayOpacity: 0.6,
  stagePadding: 6,
  stageRadius: 8,
  showProgress: true,
  // Forward only; steps override this to drop 'next'.
  showButtons: ['next', 'close'],
};

export default class extends Controller {
  static values = {
    name: String,
    payload: { type: Object, default: {} },
    completionUrl: String,
  };

  connect() {
    if (tours[this.nameValue]) return;

    // eslint-disable-next-line no-console
    console.warn(`TourController: no tour registered as "${this.nameValue}" — check app/javascript/tours/index.js`);
  }

  // A launcher control asked for this tour by name (lite's "Getting Started?" card, the tools page's "?"):
  // the only thing that ever starts a run.
  launch({ detail }) {
    if (detail?.name !== this.nameValue || !tours[this.nameValue]) return;
    // A run still in flight keeps the page: restarting would strand its restore half-done.
    if (this.driver && !this.ended) return;

    this.cleanup?.();
    this.#destroy();
    this.#mount();
  }

  disconnect() {
    this.cleanup?.();
    // The element-removal half of #watchForExit; the unload half is the one that actually fires on lite.
    this.#trackEnd('tour_skipped', LEFT_PAGE_VIA);
    this.#destroy();
  }

  #mount() {
    this.cleanup = tours[this.nameValue].mount({
      element: this.element,
      payload: this.payloadValue,
      start: (config) => this.#start(config),
    });
  }

  #start(config) {
    // Per run, so a second run of the same tour reports its steps again rather than going quiet.
    this.seenSteps = new Set();
    // Held rather than read back off the driver: getConfig() hands back the wrapper below, not the tour's own.
    this.tourConfig = config;
    this.torndown = false;
    this.ended = false;
    this.completed = false;
    this.driver = driver({
      ...DRIVER_DEFAULTS,
      ...config,
      onDestroyed: (...args) => this.#teardown(...args),
      onPopoverRender: (popover, options) => this.#renderPopover(popover, options),
      // Completed = destroy initiated on the final step (Done, or close while there); anything earlier is a
      // drop-out. Defining onDestroyStarted suppresses driver.js's own destroy, so delegate to the tour's
      // hook or destroy ourselves.
      onDestroyStarted: (...args) => {
        if (this.driver?.isLastStep()) this.#trackEnd('tour_completed');
        else this.#trackEnd('tour_skipped', 'close');
        if (config.onDestroyStarted) config.onDestroyStarted(...args);
        else this.#destroy();
      },
    });
    this.#watchForExit();
    this.#watchForModal();
    // Before drive(), which renders step 1 synchronously and reports it — a funnel ordered by timestamp
    // would otherwise see the first step arrive ahead of the tour that contains it.
    trackEvent('tour_started', this.#eventParams());
    this.driver.drive();
    return this.driver;
  }

  // Turbo Drive is off (application.js), so every navigation tears the document down whole rather than
  // swapping the body: nothing removes the element, Stimulus never disconnects, and a visitor who closes
  // the tab or navigates away mid-tour would end no run at all. pagehide over beforeunload/unload — it is
  // the only one Safari and bfcache both fire. Not visibilitychange: alt-tabbing away isn't leaving.
  #watchForExit() {
    this.onPageHide = () => this.#trackEnd('tour_skipped', LEFT_PAGE_VIA);
    window.addEventListener('pagehide', this.onPageHide);
  }

  #stopWatchingForExit() {
    if (!this.onPageHide) return;

    window.removeEventListener('pagehide', this.onPageHide);
    this.onPageHide = null;
  }

  // driver.js acts on keyup and bootstrap closes on keydown, so modal state is read at keydown: on
  // shown/hidden the Escape that just closed the modal would destroy the tour behind it too (UNS-5314).
  // Only the first keydown of a press counts — bootstrap drops .show synchronously, so an auto-repeat
  // partway through a held Escape would read no modal and hand the tour back before the keyup.
  #watchForModal() {
    this.onKeyDown = (event) => {
      if (event.repeat) return;

      this.#allowKeyboardControl(!document.querySelector('.modal.show'));
    };
    window.addEventListener('keydown', this.onKeyDown, true);
  }

  #stopWatchingForModal() {
    if (!this.onKeyDown) return;

    window.removeEventListener('keydown', this.onKeyDown, true);
    this.onKeyDown = null;
  }

  #allowKeyboardControl(allowed) {
    if (!this.driver) return;

    this.driver.setConfig({ ...this.driver.getConfig(), allowKeyboardControl: allowed });
  }

  // driver.js focuses the close button after this hook — take focus onto the popover instead, on the
  // next frame so ours wins. tabindex -1 keeps the wrapper out of driver's tab trap.
  #renderPopover(popover, { state }) {
    popover.wrapper.setAttribute('tabindex', '-1');
    requestAnimationFrame(() => popover.wrapper.focus({ preventScroll: true }));

    if (state.activeIndex === 0) popover.footer.prepend(this.#skipButton());
    this.#trackStep(state.activeIndex);
  }

  // Rendering is the only per-step hook a tour can't take away from us — steps define their own
  // onHighlighted. It can fire more than once for a step (a refresh re-renders), so each is counted once.
  // Names must match PosthogEvents::TOUR_STEP_VIEWED.
  #trackStep(index) {
    if (index === undefined || this.seenSteps.has(index)) return;

    this.seenSteps.add(index);
    if (index >= COMPLETION_STEP_INDEX) this.#recordWatched();
    trackEvent('tour_step_viewed', this.#eventParams({ step: index + 1, step_count: this.tourConfig?.steps?.length }));
  }

  // replay is constant now that a launcher press is the only way onto a tour. Still sent on every event:
  // the existing PostHog tour funnels filter on it, and dropping it would take them to zero.
  #eventParams(extra = {}) {
    return { tour_name: this.nameValue, replay: true, ...extra };
  }

  // Every destroy goes through here, and the teardown runs even when driver.js's own onDestroyed doesn't:
  // it commits the active step only once a step's stage animation settles, and destroying before that
  // skips onDestroyed outright — stranding the tour mid-restore, with the page still in its demo state.
  #destroy() {
    this.driver?.destroy();
    this.#teardown();
  }

  #teardown(...args) {
    this.#stopWatchingForExit();
    this.#stopWatchingForModal();
    if (this.torndown || !this.tourConfig) return;

    this.torndown = true;
    this.tourConfig.onDestroyed?.(...args);
    // Past the guard, so only a run that actually started announces its end — a tour-launcher control uses
    // this to show where the tour went.
    document.dispatchEvent(new CustomEvent('tour:ended', { detail: { name: this.nameValue, completed: this.completed } }));
  }

  // Exactly one per run, so the pair partitions every tour that started. `via` is what separates a visitor
  // who dismissed the tour and stayed (skip_button, close) from one who left with it still open (left_page).
  // Names must match PosthogEvents::TOUR_COMPLETED and TOUR_SKIPPED.
  #trackEnd(name, via) {
    // No driver means no run to end — the exit paths fire on every page whether or not the tour ever played.
    if (this.ended || !this.driver) return;

    this.ended = true;
    this.completed = name === 'tour_completed';
    if (this.completed) this.#recordWatched();
    const stepCount = this.tourConfig?.steps?.length;
    const step = (this.driver?.getActiveIndex() ?? 0) + 1;
    trackEvent(
      name,
      this.#eventParams({
        step_count: stepCount,
        // One ordered axis across both endings, so a bar chart sorts and averages without a formula: a drop-out
        // lands on the step it happened, a finish one past the last. step_label is the same thing to read.
        // No plain `step` beside it — it would be reached_step on a drop-out and step_count on a finish, always.
        reached_step: this.completed ? stepCount + 1 : step,
        step_label: this.completed ? COMPLETED_LABEL : `${step} of ${stepCount}`,
        ...(via ? { via } : {}),
      }),
      { beacon: via === LEFT_PAGE_VIA },
    );
  }

  // A tour whose offer is gated server-side has to report the finish to the account, or the card goes on
  // making it. Fire-and-forget: nothing on this page reads the gate again, the next load does. A write that
  // didn't land re-arms, so the end of the run is a second chance at it.
  #recordWatched() {
    if (!this.completionUrlValue || this.recordedWatched) return;

    this.recordedWatched = true;
    fetch(this.completionUrlValue, {
      method: 'PATCH',
      headers: { 'X-CSRF-Token': csrfToken(), 'Content-Type': 'application/json' },
    })
      .then((response) => {
        this.recordedWatched = response.ok;
      })
      .catch(() => {
        this.recordedWatched = false;
      });
  }

  // #destroy rather than the close-intent path: a skip is not a completion, and going straight to teardown
  // keeps it out of tour_completed while still running the tour's own onDestroyed restore.
  #skipButton() {
    const button = document.createElement('button');
    button.type = 'button';
    button.className = SKIP_CLASS;
    button.textContent = SKIP_TEXT;
    button.addEventListener('click', () => {
      this.#trackEnd('tour_skipped', 'skip_button');
      this.#destroy();
    });
    return button;
  }
}
